
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import {
  aceDarkControlClass,
  aceDarkMenuClass,
  aceDarkPanelGradientLayers,
} from '@/components/help-center/aces/AceDarkPanelShell';
import { cn } from '@/lib/utils';

type SortOption<T extends string> = { id: T; label: string };

type AceSortDropdownProps<T extends string> = {
  value: T;
  options: SortOption<T>[];
  onChange: (value: T) => void;
  ariaLabel?: string;
};

export function AceSortDropdown<T extends string>({
  value,
  options,
  onChange,
  ariaLabel = 'Ordenar',
}: AceSortDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth ?? 168;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));

    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left,
      minWidth: rect.width,
      zIndex: 60,
      visibility: 'visible',
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [open, updatePosition, value, options]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    const handleReposition = () => updatePosition();

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  const selected = options.find((option) => option.id === value);

  const handleToggle = () => {
    if (open) {
      setOpen(false);
      return;
    }

    const trigger = triggerRef.current;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left: Math.max(8, rect.right - 168),
        minWidth: rect.width,
        zIndex: 60,
        visibility: 'hidden',
      });
    }

    setOpen(true);
  };

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={ariaLabel}
            style={menuStyle}
            className={cn(aceDarkMenuClass, 'min-w-[168px]')}
          >
            <div className="relative">
              {aceDarkPanelGradientLayers}
              <ul className="relative z-10 py-1">
                {options.map((option) => {
                  const active = option.id === value;

                  return (
                    <li key={option.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          onChange(option.id);
                          setOpen(false);
                        }}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left',
                          'font-poppins text-xs font-semibold transition-colors',
                          active
                            ? 'bg-amber-400/12 text-amber-100'
                            : 'text-blue-100/80 hover:bg-white/[0.05] hover:text-blue-50'
                        )}
                      >
                        <span>{option.label}</span>
                        {active && (
                          <Check className="h-3.5 w-3.5 shrink-0 text-amber-300" aria-hidden />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={handleToggle}
        className={cn(
          aceDarkControlClass,
          'inline-flex min-w-[168px] items-center justify-between gap-2 py-2 pl-3 pr-2.5',
          'font-poppins text-xs font-semibold text-blue-50',
          open && 'border-amber-400/35 ring-2 ring-amber-400/12'
        )}
      >
        <span className="truncate">{selected?.label}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-amber-200/70 transition-transform duration-200',
            open && 'rotate-180'
          )}
          aria-hidden
        />
      </button>
      {menu}
    </>
  );
}
